import { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import isEmpty from 'lodash/fp/isEmpty';

import { Box, Button, Hidden, Text, TextLink, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { getImageProps, type StaticImageData } from 'next/image';
import { type RequireAtLeastOne } from 'type-fest';

import { CheckoutBlockReferFriend } from 'Apps/Checkout/Blocks/CheckoutBlockReferFriend';

import { BottomModal } from 'Components/BottomModal';
import { TwoUp } from 'Components/TwoUp';

import { availableCountries, countriesCode } from 'constants/countries';
import type currencies from 'constants/currencies';

import { content as skincareMinisContent } from 'assets/content/promoOrderSuccess/skincareMinis';
import { content as trialOfferContent } from 'assets/content/promoOrderSuccess/trialOffer';

import { handleKeyPress } from 'utils/a11y';
import { createTranslationFn, type I18nContentMap, interpRegExp } from 'utils/createTranslationFn';
import { formatPriceWithCurrency } from 'utils/currencies';

import { trackHeapEvent } from 'dux/tracking/actions';
import {
  getHasCompletedHaircareConsultation,
  getHasCompletedSkincareConsultation,
  getHasHaircareSubscriptionInAnyState,
  getHasSkincareSubscriptionInAnyState,
  getUserCurrency,
  getUserGeolocationGuessedCountry,
} from 'dux/user/selectors';
import { excludePromo, PromoIdentifiers, usePromoIdentifiers } from 'dux/promotions/hooks';

const renderToHTML = (content: any, Tag: React.ElementType = 'div') => {
  const markup = { __html: content };
  return <Tag dangerouslySetInnerHTML={markup} style={{ display: 'contents' }} />;
};

type EmbeddedModuleContent = {
  tracking: {
    pageView: string;
  };
  twoUp: {
    image: {
      mobile: StaticImageData;
      desktop: StaticImageData;
      imgAlt: string;
    };
    eyebrow: string;
    title: string;
    description?: string;
    cta: {
      consultationCompleted: {
        label: string;
        linkTo: string;
        dataFrom: string;
        dataClick: string;
      };
      consultationNotCompleted: {
        label: string;
        linkTo: string;
        dataFrom: string;
        dataClick: string;
      };
    };
    terms: {
      label: string;
      dataFrom: string;
      dataClick: string;
    };
  };
  bottomModal: {
    title: string;
    description: string;
    conditions: string;
    variant: string;
  };
};

type ByCategory<Content extends EmbeddedModuleContent> = RequireAtLeastOne<
  {
    haircare?: Content;
    skincare?: Content;
  },
  'haircare' | 'skincare'
>;

const ButtonWrapper = styled.div`
  width: 254px;
`;

type PostPurchaseEmbeddedModuleProps = {
  hasCompletedConsultation: { haircare: boolean; skincare: boolean };
  countryCode: (typeof availableCountries)[number];
  currency: keyof typeof currencies;
  content: I18nContentMap<ByCategory<EmbeddedModuleContent>>;
  upsellCategory: 'haircare' | 'skincare';
  promoId: string;
};

const PostPurchaseEmbeddedModule = ({
  hasCompletedConsultation,
  countryCode,
  currency,
  content: contentByCategory,
  upsellCategory,
  promoId,
}: PostPurchaseEmbeddedModuleProps) => {
  const t = createTranslationFn(contentByCategory, countryCode, countriesCode.US);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(trackHeapEvent(t(`${upsellCategory}.tracking.pageView`)));
  }, [dispatch]);

  const common = {
    alt: 'foo',
    fetchPriority: 'high',
    priority: true,
    placeholder: 'blur',
    fill: true,
    style: {
      objectFit: 'cover',
    },
  } as const;
  const {
    props: { srcSet: mobile, ...rest },
  } = getImageProps({
    ...common,
    /* width: 1080, height: 1920,  */ src: t(`${upsellCategory}.twoUp.image.mobile`),
    // sizes: '50vw',
  });

  const {
    props: { srcSet: desktop },
  } = getImageProps({
    ...common,
    /* width: 1200,  height: 1600, */ src: t(`${upsellCategory}.twoUp.image.desktop`),
    // sizes: '100vw',
  });

  const formatCurrency = (_matched: any, p1: string) =>
    formatPriceWithCurrency({ price: p1.split(':')[1]!, currency, minimumFractionDigits: 0 });

  const bottomModalContent = {
    title: t(`${upsellCategory}.bottomModal.title`),
    description: renderToHTML(
      t(`${upsellCategory}.bottomModal.description`).replace(interpRegExp, formatCurrency),
    ),
    conditions: renderToHTML(
      t(`${upsellCategory}.bottomModal.conditions`).replace(interpRegExp, formatCurrency),
    ),
  };

  const ctaProps = hasCompletedConsultation[upsellCategory!]
    ? t(`${upsellCategory}.twoUp.cta.consultationCompleted`)
    : t(`${upsellCategory}.twoUp.cta.consultationNotCompleted`);

  return (
    <>
      <BottomModal
        backgroundColor={
          t(`${upsellCategory}.bottomModal.variant`) === 'subscription'
            ? theme.colors.highlight[200]
            : theme.colors.tertiary[200]
        }
        content={bottomModalContent}
        dataFrom=""
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
      <TwoUp
        desktopOrder="21"
        minHeight={{ desktop: '395px', mobile: 'fit-content' }}
        spaceDistribution={{ desktop: 'equal', mobile: 'equal' }}
      >
        <Box
          alignItems={{ mobile: 'center', tablet: 'flex-start' }}
          backgroundColor="primary.300"
          display="flex"
          flexDirection="column"
          gap={{ mobile: '6x', tablet: '8x' }}
          justifyContent="center"
          minHeight={0}
          paddingX={{ mobile: '8x', tablet: '15x', desktop: '30x' }}
          paddingY={{ mobile: '8x', tablet: '15x', desktop: '30x' }}
          width="full"
        >
          {/* Stack */}
          <Box
            alignItems={{ mobile: 'center', tablet: 'flex-start' }}
            display="flex"
            flexDirection="column"
            gap="2x"
            justifyContent="space-between"
          >
            <Text color="neutral.100" fontWeight="medium" variant="labelSm">
              {t(`${upsellCategory}.twoUp.eyebrow`)}
            </Text>
            <Hidden above="mobile">
              <Text
                color="neutral.100"
                data-testid={`post-purchase-embedded-module-title-${promoId}`}
                textAlign="center"
                variant="heading6"
              >
                {t(`${upsellCategory}.twoUp.title`)}
              </Text>
              {!isEmpty(t(`${upsellCategory}.twoUp.description`)) && (
                <Text asChild color="neutral.100" textAlign="center" variant="bodyLg">
                  {renderToHTML(
                    t(`${upsellCategory}.twoUp.description`).replace(interpRegExp, formatCurrency),
                    'p',
                  )}
                </Text>
              )}
            </Hidden>
            <Hidden below="tablet">
              <Text
                color="neutral.100"
                data-testid={`post-purchase-embedded-module-title-${promoId}`}
                variant="heading4"
              >
                {t(`${upsellCategory}.twoUp.title`)}
              </Text>
              {!isEmpty(t(`${upsellCategory}.twoUp.description`)) && (
                <Text asChild color="neutral.100" variant="bodyLg">
                  {renderToHTML(
                    t(`${upsellCategory}.twoUp.description`).replace(interpRegExp, formatCurrency),
                    'p',
                  )}
                </Text>
              )}
            </Hidden>
          </Box>
          <Hidden above="mobile">
            <ButtonWrapper>
              <Button asChild mode="dark" size="md" variant="primary">
                <Link
                  data-click={ctaProps.dataClick}
                  data-from={ctaProps.dataFrom}
                  data-testid={`post-purchase-embedded-module-cta-${promoId}`}
                  to={ctaProps.linkTo}
                >
                  {ctaProps.label}
                </Link>
              </Button>
            </ButtonWrapper>
          </Hidden>
          <Hidden below="tablet">
            <Button asChild mode="dark" size="full" variant="primary">
              <Link
                data-click={ctaProps.dataClick}
                data-from={ctaProps.dataFrom}
                data-testid={`post-purchase-embedded-module-cta-${promoId}`}
                to={ctaProps.linkTo}
              >
                {ctaProps.label}
              </Link>
            </Button>
          </Hidden>
          <TextLink
            data-click={t(`${upsellCategory}.twoUp.terms.dataClick`)}
            data-from={t(`${upsellCategory}.twoUp.terms.dataFrom`)}
            data-testid={`post-purchase-embedded-module-terms-${promoId}`}
            mode="dark"
            onClick={() => setIsOpen(true)}
            onKeyDown={handleKeyPress(() => setIsOpen(true))}
            variant="simple"
          >
            {t(`${upsellCategory}.twoUp.terms.label`)}
          </TextLink>
        </Box>
        <Box __minHeight="fit-content" position="relative" width="full">
          <picture>
            <source media="(min-width: 63.9em)" srcSet={desktop} />
            <source media="(min-width: 0)" srcSet={mobile} />
            <img {...rest} alt={t(`${upsellCategory}.twoUp.image.imgAlt`)} />
          </picture>
        </Box>
      </TwoUp>
    </>
  );
};

export const OrderSuccessPromoCommunication = ({
  category,
  promoOverride,
}: {
  category?: 'haircare' | 'skincare';
  promoOverride?: Exclude<PromoIdentifiers, 'feedback'>;
}) => {
  const { availablePromoList } = usePromoIdentifiers();
  const country = useAppSelector<(typeof availableCountries)[number]>(
    getUserGeolocationGuessedCountry,
  );
  const currency = useAppSelector<keyof typeof currencies>(getUserCurrency);
  const hasCompletedSkincareConsultation = useAppSelector(getHasCompletedSkincareConsultation);
  const hasCompletedHaircareConsultation = useAppSelector(getHasCompletedHaircareConsultation);
  const hasSkinSub = useAppSelector(getHasSkincareSubscriptionInAnyState);
  const hasHairSub = useAppSelector(getHasHaircareSubscriptionInAnyState);
  const promoIdentifier = promoOverride ?? excludePromo(availablePromoList, 'feedback')[0]?.id;

  switch (promoIdentifier) {
    case PromoIdentifiers.SKINCARE_MINIS:
      return (
        <PostPurchaseEmbeddedModule
          content={skincareMinisContent}
          countryCode={country}
          currency={currency}
          hasCompletedConsultation={{
            skincare: hasCompletedSkincareConsultation,
            haircare: hasCompletedHaircareConsultation,
          }}
          promoId={promoIdentifier}
          upsellCategory="skincare"
        />
      );
    case PromoIdentifiers.TRIAL_OFFER: {
      let upsellCategory: 'haircare' | 'skincare' | undefined;
      switch (category) {
        case 'haircare':
          if (!hasSkinSub) {
            upsellCategory = 'skincare';
          }
          break;
        case 'skincare':
          if (!hasHairSub) {
            upsellCategory = 'haircare';
          }
          break;
        default:
          upsellCategory = !hasHairSub ? 'haircare' : !hasSkinSub ? 'skincare' : undefined;
      }

      if (upsellCategory) {
        return (
          <PostPurchaseEmbeddedModule
            content={trialOfferContent}
            countryCode={country}
            currency={currency}
            hasCompletedConsultation={{
              skincare: hasCompletedSkincareConsultation,
              haircare: hasCompletedHaircareConsultation,
            }}
            promoId={promoIdentifier}
            upsellCategory={upsellCategory}
          />
        );
      }
    }
    // fallthrough
    default:
      return <CheckoutBlockReferFriend category={category} />;
  }
};
